import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { WBSButton } from '@/common/WBSButton';
import { WBSCheckbox } from '@/common/WBSCheckbox';
import { FollowUpPartnerPicker } from '@/follow-up/FollowUpPartnerPicker';
import {
  SectionHeading,
  ViewBody,
  ViewContainer,
  ViewHeader,
  ViewHeaderTitle,
} from '@/follow-up/followUpStyles';
import { StudentLocationNotice } from '@/follow-up/StudentLocationNotice';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { formatString } from '~/utils/stringFormat';

type FollowUpPartnerOption = components['schemas']['FollowUpPartnerOption'];

export const Route = createFileRoute('/_app/follow-ups/entries/convert')({
  component: ConvertRequestScreen,
  // Legacy's `request` parameter, passed by both entry points: the coordinator
  // queue's Assign to Partner action and the board's Take redirect.
  validateSearch: (search): { request?: number } => ({
    request: search.request ? Number(search.request) : undefined,
  }),
});

/**
 * Convert: a coordinator who has claimed a request picks a follow-up partner
 * near the student, writes them a message, and presses Assign. That creates the
 * follow-up, copying the request's snapshots and inviting one partner
 * (`partner_id` stays null until they accept), and moves the request to
 * `in_progress`.
 *
 * The objective list is display only: legacy posts it and ignores it,
 * re-reading the request's own objectives, so the boxes render checked and
 * disabled.
 *
 * Legacy's "Search partners by location" map isn't built: it applied no
 * distance filter and showed the same set the picker returns. The picker's
 * empty state replaces it.
 */
function ConvertRequestScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { request: requestId } = Route.useSearch();
  const { me, isPending: mePending } = useCurrentUser();

  const [partner, setPartner] = useState<FollowUpPartnerOption | null>(null);
  const [message, setMessage] = useState('');

  const {
    data: draft,
    isLoading,
    isError,
  } = api.getFollowUpConvertDraft.useQuery(
    { path: { request_id: requestId ?? 0 } },
    { enabled: requestId !== undefined },
  );

  const {
    mutateAsync: createFollowUp,
    isPending: assigning,
    error: assignError,
  } = api.createFollowUp.useMutation();

  const handleAssign = async (): Promise<void> => {
    if (requestId === undefined || partner === null) return;
    let created: components['schemas']['FollowUpConversionResult'];
    try {
      created = await createFollowUp({
        body: {
          request_id: requestId,
          partner_id: partner.id,
          message_for_partner: message,
        },
      });
    } catch {
      // Rendered from `assignError` — 403 for a request another coordinator
      // adopted or a partner outside the picker set, 409 for a request that is
      // no longer `adopted` or already has a follow-up.
      return;
    }
    // The request leaves the coordinator's queue and the new follow-up joins
    // their entries list.
    await Promise.all([
      api.listCoordinatorPendingFollowUpRequests.invalidateQueries(),
      api.listCoordinatorFollowUps.invalidateQueries(),
      api.listFollowUpRequestBoard.invalidateQueries(),
    ]);
    // Legacy lands on the new follow-up's detail after assigning.
    await navigate({
      to: '/follow-ups/entries/view/$followUpId/',
      params: { followUpId: String(created.id) },
    });
  };

  // No admin arm: creating the follow-up and reading the draft both require the
  // coordinator who claimed the request.
  if (mePending || isLoading) return null;
  if (me?.profile_type !== 'FOLLOWUP_COORDINATOR') return <NotFoundScreen />;
  // 404, not 403, for a request outside the caller's scope, so the read isn't
  // an existence oracle for other coordinators' requests.
  if (requestId === undefined || isError || draft === undefined) {
    return <NotFoundScreen />;
  }

  return (
    <PageContainer>
      <ViewContainer>
        <ViewHeader>
          <ViewHeaderTitle>{t('wbs.new-follow-up')}</ViewHeaderTitle>
        </ViewHeader>
        <ViewBody>
          <SectionHeading>
            {formatString(
              t('wbs.create-follow-up-from-request'),
              String(draft.request_id),
            )}
          </SectionHeading>

          <SectionHeading>
            {t('lessons.target-student')}{' '}
            {draft.student !== null && (
              <StudentLink>
                <Link
                  to="/members/$memberId/profile/"
                  params={{ memberId: String(draft.student.id) }}
                >
                  {draft.student.display_name}
                </Link>
              </StudentLink>
            )}
          </SectionHeading>

          <Section>
            <SectionHeading>{t('wbs.follow-up-partner')}</SectionHeading>
            {/* The picker's filters (the request's country, the student's
               language, and verified or created by me) are resolved
               server-side from `request_id`, so nothing tamperable is
               passed from here. */}
            <FollowUpPartnerPicker
              requestId={draft.request_id}
              value={partner}
              onChange={setPartner}
            />
          </Section>

          <Section>
            <SectionHeading>{t('wbs.objective')}</SectionHeading>
            <ObjectiveList>
              {draft.selected_requests.map((option) => (
                <ObjectiveRow key={option.value}>
                  <WBSCheckbox checked disabled readOnly onChange={() => {}} />
                  <span>{option.label}</span>
                </ObjectiveRow>
              ))}
            </ObjectiveList>
          </Section>

          <Section>
            <SectionHeading>
              {t('wbs.message-to-follow-up-partner')}
            </SectionHeading>
            <MessageBox
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={5}
              data-testid="message-for-partner"
            />
          </Section>

          {/* The map isn't built, but legacy's student address under it is. */}
          <StudentLocationNotice address={draft.student_contact_info.address} />

          {assignError != null && (
            <AlertBox variant="error">
              {errorMessage(
                assignError,
                t('wbs.there-was-a-problem-with-your-submission'),
              )}
            </AlertBox>
          )}

          <Actions>
            {/* Legacy's Cancel targets a path that doesn't exist and falls
               back to pending requests, so Cancel goes to the queue. */}
            <WBSButton
              alternate
              as={Link}
              to="/follow-ups/pending-requests/"
              data-testid="cancel-convert"
            >
              {t('actions.cancel')}
            </WBSButton>
            {/* Disabled until a partner is picked. Legacy has no
               empty-selection check here and would write a follow-up
               naming nobody. */}
            <WBSButton
              type="button"
              onClick={() => void handleAssign()}
              isLoading={assigning}
              disabled={partner === null}
              data-testid="assign-follow-up-partner"
            >
              {t('wbs.assign')}
            </WBSButton>
          </Actions>
        </ViewBody>
      </ViewContainer>
    </PageContainer>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/** A wrapper rather than `styled(Link)`: wrapping the component erases its
 *  route-typed `to`/`params` props, so the compiler stops checking the target. */
const StudentLink = styled.span`
  a {
    color: var(--wbs-red);
    text-decoration: none;
  }
`;

const ObjectiveList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ObjectiveRow = styled.label`
  align-items: center;
  display: flex;
  font-size: 14px;
  gap: 10px;
`;

const MessageBox = styled.textarea`
  background: var(--grey-100);
  border: none;
  border-radius: 6px;
  box-shadow: var(--shadow-inset-md);
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  padding: 12px 14px;
  resize: vertical;
  width: 100%;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;
